import {AfterViewInit, Component, ElementRef, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {zip} from 'rxjs';
import {finalize} from 'rxjs/operators';

import {Chart, ChartDataSets} from 'chart.js';
import * as moment from 'moment';

import { NavService } from 'shared/nav/nav.service';
import {Averages, StatusData} from '../../model/status-data';
import {StatusService} from '../../services/status.service';
import { UserService } from 'app/profile';
import { Platform } from '@ionic/angular';

type IndicatorValue = 'inactive' | 'low' | 'medium' | 'high';
const MEDIUM_VALUE: number = 1.4;
const LOW_VALUE: number = 2.6;

const INACTIVE_INDICATORS: Array<IndicatorValue> = ['inactive', 'inactive', 'inactive', 'inactive'];

function getIndicatorValue(average: number): IndicatorValue {
  if (average < 0) {
    return 'inactive';
  }
  if (average < MEDIUM_VALUE) {
    return 'high';
  }
  if (average < LOW_VALUE) {
    return 'medium';
  }

  return 'low';
}

function getIndicatorValuesFromAverages(averages: Averages): Array<IndicatorValue> {
  return [
    getIndicatorValue(averages.currentWeek),
    getIndicatorValue(averages.prevWeek),
    getIndicatorValue(averages.month),
    getIndicatorValue(averages.year),
  ];
}

const LABEL_FORMAT: string = 'D.M.';

@Component({
  selector: 'recreation-panel',
  templateUrl: 'recreation-panel.html',
  styleUrls: ['recreation-panel.scss'],
})
export class RecreationPanel implements AfterViewInit {

  @ViewChild('chartCanvas', {static: true}) private _canvasRef: ElementRef;

  private _chart: Chart;
  private _datasetLabels: Array<string>;
  private _statusData: StatusData;
  private _loadingData: boolean;

  private _datasetAverageIndex: number = 0;
  private _datasetRecoveryPosIndex: number = 1;
  private _datasetRecoveryNegIndex: number = 2;

  constructor(
    public navService: NavService,
    public translateService: TranslateService,
    public statusService: StatusService,
    public userService: UserService,
    private platform: Platform) { }

  private static createEmptyData(): Array<number> {
    const dayCount: number = 7;
    return new Array<number>(dayCount).fill(null);
  }

  private static createLabels(start: Date): Array<string> {
    const date: moment.Moment = moment(start);

    const labels: Array<string> = [date.format(LABEL_FORMAT)];

    const entryCount: number = 6;

    for (let i: number = 0; i < entryCount; i++) {
      date.add(1, 'day');
      labels.push(date.format(LABEL_FORMAT));
    }

    return labels;
  }

  public get detachmentAverages(): Array<IndicatorValue> {
    if (!this._statusData) {
      return INACTIVE_INDICATORS;
    }
    return getIndicatorValuesFromAverages(this._statusData.detachment);
  }

  public get relaxationAverages(): Array<IndicatorValue> {
    if (!this._statusData) {
      return INACTIVE_INDICATORS;
    }
    return getIndicatorValuesFromAverages(this._statusData.rest);
  }

  public get experienceAverages(): Array<IndicatorValue> {
    if (!this._statusData) {
      return INACTIVE_INDICATORS;
    }
    return getIndicatorValuesFromAverages(this._statusData.experience);
  }

  public get loadingData(): boolean {
    return this._loadingData;
  }

  public ngAfterViewInit(): void {
    this._loadingData = true;

    const weekInterval: number = 6;

    const endDate: Date = moment().endOf('day').toDate();
    const startDate: Date = moment(endDate).subtract(weekInterval, 'days').startOf('day').toDate();

   zip(
      this.translateService.get('STATUS.AVERAGE_RELAXATION'),
      this.translateService.get('STATUS.RECOVERY'),
      this.translateService.get('STATUS.FATIGUE'),
    )
      .subscribe((translations: Array<string>) => {
        this._datasetLabels = translations;
        this.initChart(startDate);
        this.getStatusData(startDate);
      });
  }

  private getStatusData(startDate: Date): void {
    this.statusService.getStatusData()
      .pipe(finalize(() => { this._loadingData = false; }))
      .subscribe((data: StatusData) => {
        const datasets: Array<ChartDataSets> = this._chart.data['datasets'];
        this._statusData = data;

        const recoveryPositiveDataSet: ChartDataSets = datasets[this._datasetRecoveryPosIndex];
        recoveryPositiveDataSet.data = data.weekRest.map((entry) => { return this.mapRestEntry(entry); });

        const recoveryNegativeDataSet: ChartDataSets = datasets[this._datasetRecoveryNegIndex];
        recoveryNegativeDataSet.data = data.weekRest.map((entry) => { return this.mapRestEntry(entry, false); });

        const averageData: Array<number> = this.createAverageValues(data);
        const averageDataSet: ChartDataSets = datasets[this._datasetAverageIndex];
        averageDataSet.data = averageData;

        this._chart.update();
      });
  }

  private initChart(start: Date): void {

    const canvas: HTMLCanvasElement = this._canvasRef.nativeElement;
    const isDeka = false; //(this.userService.getTheme() === 'deka' || this.userService.getTheme() === 'fcb');

    let fontColor = 'white';
    let fontColor05 = 'rgba(255, 255, 255, 0.5)';
    try {
      fontColor = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--app-text-color');
      fontColor05 = getComputedStyle(document.getElementsByClassName('ion-page')[0]).getPropertyValue('--app-text-color05');
    } catch (e) {
      // ignore
    }

    this._chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: RecreationPanel.createLabels(start),
        datasets: [
          {
            type: 'line',
            label: this._datasetLabels[this._datasetAverageIndex],
            backgroundColor: 'rgba(40, 98, 191, '+(isDeka ? 1.0 : 0.5)+')',
            borderColor: 'rgba(40, 98, 191, '+(isDeka ? 1.0 : 0.5)+')',
            borderWidth: 2,
            pointRadius: 0,
            fill: this.platform.is('ios'), // at iOS the fill: false letztthe wohl lne vanish
          },
          {
            type: 'bar',
            label: this._datasetLabels[this._datasetRecoveryPosIndex],
            backgroundColor: 'rgba(106, 180, 67, '+(isDeka ? 1.0 : 0.5)+')',
            borderColor: 'rgba(0, 0, 0, 0)',
            borderWidth: 1,
          },
          {
            type: 'bar',
            label: this._datasetLabels[this._datasetRecoveryNegIndex],
            backgroundColor: 'rgba(206, 116, 55, '+(isDeka ? 1.0 : 0.5)+')',
            borderColor: 'rgba(0, 0, 0, 0)',
            borderWidth: 1,
          },
        ],
      },
      options: {
        legend: {
          position: 'bottom',
          labels: {
            fontColor: fontColor,
            boxWidth: 16,
          },
          onClick: (event, item) => { event.stopImmediatePropagation(); },
        },
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          yAxes: [
            {
              id: 'y-axis-0',
              position: 'left',
              display: true,
              ticks: {
                min: -100,
                max: 100,
                fontColor: fontColor,
              },
              gridLines: {
                color: fontColor05,
                zeroLineColor: fontColor05,
                drawBorder: false,
              },
              stacked: true,
            },
            {
              id: 'y-axis-1',
              position: 'right',
              display: true,
              ticks: {
                min: -100,
                max: 100,
                fontColor: fontColor,
              },
              gridLines: {
                color: fontColor05,
                zeroLineColor: fontColor05,
                drawBorder: false,
              },
              stacked: true,
            },
            // tslint:disable-next-line:no-any
          ] as Array<any>,
          xAxes: [{
            ticks: {
              fontColor: fontColor,
            },
            gridLines: {
              display: false,
              drawTicks: false,
            },
            // tslint:disable-next-line:no-any
          }] as Array<any>,
        },
      },
    });

  }

  private mapRestEntry(value: number, positive: boolean = true): number {
    if (positive && value > 0) {
      return value;
    }
    if (!positive && value < 0) {
      return value;
    }
    return null;
  }

  private createAverageValues(data: StatusData): Array<number> {
    const dayCount: number = 7;
    return new Array<number>(dayCount).fill(data.averageRest);
  }

}
